/**
 * The two icon views — docs/06-UI-SPEC.md §11.1.
 *
 * Large is for finding a lesson you half-remember. Extra Large is for reading one
 * without opening it, which SRS I5 makes a requirement rather than a nicety: the
 * preview is drawn well above screen resolution so the handwriting survives being
 * shown this big.
 */

import { thumbnailUrl, type Lecture } from '../../lectures.js';
import { StarButton } from './StarButton.js';

export interface IconGridProps {
  readonly lectures: readonly Lecture[];
  readonly size: 'large' | 'extraLarge';
  readonly onOpen: (lecture: Lecture) => void;
  readonly onStar: (lecture: Lecture) => void;
}

/** The two tile sizes §11.1 gives, and how many fit across at each. */
const TILES = {
  large: { width: 'w-[200px]', height: 'h-[150px]' },
  extraLarge: { width: 'w-[420px]', height: 'h-[320px]' },
} as const;

function Tile({
  lecture,
  size,
  onOpen,
  onStar,
}: {
  readonly lecture: Lecture;
  readonly size: 'large' | 'extraLarge';
  readonly onOpen: () => void;
  readonly onStar: () => void;
}) {
  const tile = TILES[size];
  return (
    <div
      role="button"
      tabIndex={0}
      onDoubleClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen();
      }}
      className={`relative flex ${tile.width} max-w-full flex-col gap-2 rounded-lg border border-border bg-surface p-3 text-left transition duration-fast ease-out hover:-translate-y-[2px] hover:shadow-md`}
    >
      <span className="absolute right-2 top-2 z-10 rounded-md bg-surface/80">
        <StarButton starred={lecture.isStarred} onToggle={onStar} />
      </span>
      <span
        className={`flex ${tile.height} w-full items-center justify-center overflow-hidden rounded-md bg-canvas`}
      >
        {lecture.hasThumbnail ? (
          <img
            src={thumbnailUrl(lecture)}
            alt={`The board from ${lecture.topic}`}
            loading="lazy"
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="text-base text-text-placeholder">No preview</span>
        )}
      </span>
      <span className="truncate text-sm text-text-primary" title={lecture.filename}>
        {lecture.filename}
      </span>
    </div>
  );
}

export function IconGrid({ lectures, size, onOpen, onStar }: IconGridProps) {
  return (
    <div className="flex w-full flex-wrap gap-4">
      {lectures.map((lecture) => (
        <Tile
          key={lecture.id}
          lecture={lecture}
          size={size}
          onOpen={() => {
            onOpen(lecture);
          }}
          onStar={() => {
            onStar(lecture);
          }}
        />
      ))}
    </div>
  );
}
